/// <amd-module name="VSSUI/Utilities/ItemContribution/ItemContribution" />
import { ObservableArray } from "VSS/Core/Observable";
import { IItemProvider, IItemWithIdProvider } from "./interfaces";

/**
 * Given an IItemProvider, implements an ObservableArray that contains the items provided by the provider, updates existing items if exists using identifier.
 */
/** @deprecated */
export class ContributedItemWithIdArray<TContributedItem, TItem> extends ObservableArray<TItem> {
    constructor(itemProvider: IItemWithIdProvider<TContributedItem>, mapDelegate: (item: TContributedItem) => TItem, getId: (item: TItem) => string) {
        super([]);

        itemProvider.loadItems((items: TContributedItem[], removedItems?: string[]) => {
            // create map of existing items
            const existingItemsMap: { [id: string]: TItem } = {};
            removedItems = removedItems || [];
            this.value.forEach(item => {
                const id = getId(item);
                if (removedItems.indexOf(id) === -1) {
                    existingItemsMap[id] = item;
                }
            });

            // update existing items
            (items || []).map(mapDelegate).forEach(item => {
                existingItemsMap[getId(item)] = item;
            });

            // populate all new items
            let newItems: TItem[] = [];
            Object.keys(existingItemsMap).forEach(key => {
                newItems.push(existingItemsMap[key]);
            });
            this.value = newItems;
        });
    }
}

/**
 * Given an IItemProvider, implements an ObservableArray that contains the items provided by the provider, replaces existing items from provider.
 */
/** @deprecated */
export class ContributedItemArray<TContributedItem, TItem> extends ObservableArray<TItem> {
    constructor(itemProvider: IItemProvider<TContributedItem>, mapDelegate: (item: TContributedItem) => TItem) {
        super([]);

        itemProvider.loadItems((items: TContributedItem[]) => {
            // When contributed items become available, update observable array
            this.value = (items || []).map(mapDelegate);
        });
    }
}

/**
 * Implements an IItemProvider that provides modified versions of items it gets from the source provider.
 */
/** @deprecated */
export class MutatedItemProvider<TInItem, TOutItem = TInItem> implements IItemProvider<TOutItem> {
    /**
     * @param provider provider that provides the source items
     * @param mutator function that modifies the items before providing them to this object's listener
     */
    public constructor(private provider: IItemProvider<TInItem>, private mutator: (item: TInItem) => TOutItem) {}

    public loadItems(itemsUpdated: (items: TOutItem[]) => void): void {
        this.provider.loadItems(items => itemsUpdated((items || []).map(this.mutator)));
    }
}

/** @deprecated */
export class PromiseWrappingItemProvider<TItem> implements IItemProvider<Promise<TItem[]>> {
    public constructor(private provider: IItemProvider<TItem>) {}

    public loadItems(itemsUpdated: (items: Promise<TItem[]>[]) => void): void {
        if (itemsUpdated) itemsUpdated([this.getItems()]);
    }

    public getItems(): Promise<TItem[]> {
        let promise: Promise<TItem[]>;
        this.provider.loadItems(items => {
            promise = new Promise<TItem[]>(resolve => resolve(items));
        });
        return promise;
    }
}

/** @deprecated */
export class ObservableArrayWrappingItemProvider<TItem> implements IItemProvider<ObservableArray<TItem>> {
    private loaded = false;
    private observableArray = new ObservableArray<TItem>([]);

    public constructor(private provider: IItemProvider<TItem>) {}

    loadItems(itemsUpdated: (items: ObservableArray<TItem>[]) => void): void {
        if (itemsUpdated) itemsUpdated([this.getItems()]);
    }

    public getItems(): ObservableArray<TItem> {
        if (!this.loaded) {
            this.provider.loadItems(items => {
                this.loaded = true;
                this.observableArray.push(...items);
            });
        }
        return this.observableArray;
    }
}
